import React, { useState, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, Users, Globe, User } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import config from "@/config";

export type Invitation = {
  id: string;
  organisation_id: string;
  organisations: {
    name: string;
  };
  website_id: string;
  websites: {
    name: string;
    url: string;
  };
  role: string;
}

interface InvitationPopupProps {
  isOpen: boolean;
  onClose: () => void;
  invitation: Invitation;
  onJoinSuccess: (invitationId: string) => void;
  onRejectSuccess?: (invitationId: string) => void;
}

const InvitationPopup: React.FC<InvitationPopupProps> = ({
  isOpen,
  onClose,
  invitation,
  onJoinSuccess,
  onRejectSuccess,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [action, setAction] = useState<"join" | "reject" | null>(null);

  // Memoized computed values
  const organisationName = useMemo(() => {
    return invitation?.organisations?.name || "Unknown Organisation";
  }, [invitation?.organisations?.name]);

  const websiteName = useMemo(() => {
    return invitation?.websites?.name || "Unknown Website";
  }, [invitation?.websites?.name]);

  const userRole = useMemo(() => {
    return invitation?.role || "member";
  }, [invitation?.role]);

  const isAdmin = useMemo(() => {
    return userRole === "admin";
  }, [userRole]);

  const isJoinLoading = useMemo(() => {
    return isLoading && action === "join";
  }, [isLoading, action]);

  const isRejectLoading = useMemo(() => {
    return isLoading && action === "reject";
  }, [isLoading, action]);

  const isAnyLoading = useMemo(() => {
    return isLoading;
  }, [isLoading]);

  const invitationDetails = useMemo(() => (
    <div className="bg-gray-50 rounded-lg p-4 space-y-3">
      <div className="flex items-center gap-2">
        <Users className="h-4 w-4 text-gray-600" />
        <span className="font-medium">Organisation:</span>
        <span className="text-gray-700">{organisationName}</span>
      </div>

      <div className="flex items-center gap-2">
        <Globe className="h-4 w-4 text-gray-600" />
        <span className="font-medium">Website:</span>
        <span className="text-gray-700">{websiteName}</span>
      </div>

      <div className="flex items-center gap-2">
        <User className="h-4 w-4 text-gray-600" />
        <span className="font-medium">Your role:</span>
        <span className="text-gray-700 capitalize">{userRole}</span>
      </div>
    </div>
  ), [organisationName, websiteName, userRole]);

  const capabilitiesList = useMemo(() => (
    <div className="text-sm text-gray-600">
      <p>You'll be able to:</p>
      <ul className="list-disc list-inside mt-1 space-y-1">
        <li>Access the website content management</li>
        <li>Create and edit content</li>
        <li>Collaborate with team members</li>
        {isAdmin && <li>Manage team members and settings</li>}
      </ul>
    </div>
  ), [isAdmin]);

  const handleJoin = async () => {
    setIsLoading(true);
    setAction("join");

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        throw new Error("No active session found");
      }

      const response = await fetch(
        `${config.supabase.functionsUrl}/join-website-invitation`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${sessionData.session.access_token}`,
          },
          body: JSON.stringify({
            userId: sessionData.session.user.id,
            invitationId: invitation.id,
            organisationId: invitation.organisation_id,
            websiteId: invitation.website_id,
            role: invitation.role,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to join website");
      }

      const result = await response.json();
      console.log("Join invitation result:", result);

      toast.success("Successfully joined the team!");
      onJoinSuccess(invitation.id);
    } catch (error) {
      console.error("Error joining invitation:", error);
      toast.error("Failed to join the team. Please try again.");
    } finally {
      setIsLoading(false);
      setAction(null);
    }
  };

  const handleReject = async () => {
    setIsLoading(true);
    setAction("reject");

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        throw new Error("No active session found");
      }

      const response = await fetch(
        `${config.supabase.functionsUrl}/reject-website-invitation`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${sessionData.session.access_token}`,
          },
          body: JSON.stringify({
            invitationId: invitation.id,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to reject invitation");
      }

      const result = await response.json();
      console.log("Reject invitation result:", result);

      toast.success("Invitation rejected");
      onRejectSuccess?.(invitation.id);
    } catch (error) {
      console.error("Error rejecting invitation:", error);
      toast.error("Failed to reject invitation. Please try again.");
    } finally {
      setIsLoading(false);
      setAction(null);
    }
  };

  const rejectButton = useMemo(() => (
    <Button
      variant="outline"
      onClick={handleReject}
      disabled={isAnyLoading}
      className="flex-1"
    >
      {isRejectLoading ? (
        <Loader2 className="h-4 w-4 animate-spin mr-2" />
      ) : null}
      Reject
    </Button>
  ), [isAnyLoading, isRejectLoading, handleReject]);

  const joinButton = useMemo(() => (
    <Button onClick={handleJoin} disabled={isAnyLoading} className="flex-1">
      {isJoinLoading ? (
        <Loader2 className="h-4 w-4 animate-spin mr-2" />
      ) : null}
      Join Team
    </Button>
  ), [isAnyLoading, isJoinLoading, handleJoin]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-blue-600" />
            You've been invited!
          </DialogTitle>
          <DialogDescription>
            You've been invited to join a team and collaborate on content creation.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {invitationDetails}

          {capabilitiesList}
        </div>

        <DialogFooter className="flex gap-2">
          {rejectButton}
          {joinButton}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default InvitationPopup;
